import React, { ReactElement, useId } from 'react';

import useFeatureToggleService from 'feature-toggle';
import styles from './BorderedBox.module.scss';
import Right from '../Right';
import useCollapsibleSectionState from './useCollapsibleSectionState';
import ShadcnBorderedBox from './ShadcnBorderedBox';

export interface BorderedBoxProps {
  title: string
  count?: number
  children?: ListOrSingle<ReactElement | boolean>
  className?: string
  headerRight?: ReactElement,
  testId?: string
  collapsible?: boolean
  sectionId?: string
  pageContext?: string
}

function LegacyBorderedBox(props: BorderedBoxProps) {
  const contentId = useId();
  const { isCollapsed, toggleSection } = useCollapsibleSectionState(props.pageContext);

  const collapsed = props.collapsible && props.sectionId
    ? isCollapsed(props.sectionId)
    : false;

  const handleToggle = () => {
    if (props.collapsible && props.sectionId) {
      toggleSection(props.sectionId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (props.collapsible && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      handleToggle();
    }
  };

  const titleClassName = props.collapsible
    ? `${styles.title} ${styles.collapsible} ${collapsed ? styles.collapsed : ''}`
    : styles.title;

  return (
    <div className={`${styles.box} ${props.className || ''}`} data-testid={props.testId}>
      {props.headerRight && !props.collapsible && (
        <Right>
          {props.headerRight}
        </Right>
      )}
      <div
        className={titleClassName}
        onClick={props.collapsible ? handleToggle : undefined}
        onKeyDown={props.collapsible ? handleKeyDown : undefined}
        role={props.collapsible ? 'button' : undefined}
        tabIndex={props.collapsible ? 0 : undefined}
        aria-expanded={props.collapsible ? !collapsed : undefined}
        aria-controls={props.collapsible ? contentId : undefined}
      >
        <span>
          {props.title}
          {props.count !== undefined && (
            <span style={{ fontWeight: 'normal', color: '#94a3b8', marginLeft: '0.25rem' }}>
              ({props.count})
            </span>
          )}
        </span>
        {props.collapsible && props.headerRight && (
          <span
            className={`${styles.headerRight} ${collapsed ? styles.headerRightHidden : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            {props.headerRight}
          </span>
        )}
      </div>
      <div
        id={contentId}
        className={`${styles.content} ${collapsed ? styles.contentCollapsed : ''}`}
      >
        {props.children}
      </div>
    </div>
  );
}

export default function BorderedBox(props: BorderedBoxProps) {
  const { isShadcnUiEnabled } = useFeatureToggleService();

  if (isShadcnUiEnabled()) {
    return <ShadcnBorderedBox {...props} />;
  } else {
    return <LegacyBorderedBox {...props} />;
  }
}
